import React, { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Typography, Button, Paper, LinearProgress, Alert, Chip,
  Select, MenuItem, FormControl, InputLabel, Stepper, Step, StepLabel,
  CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableRow, TableCell,
} from '@mui/material'
import { Upload, CheckCircle, ArrowLeft, ArrowRight, RotateCcw, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { uploadDocument, publishDocument, resetAddFlow } from '../../../redux/actions/dataUpdateAction'
import { backendClient } from '../../../api/backend'
import MetadataEditor from './MetadataEditor'
import RichHtmlEditor from './RichHtmlEditor'

export default function AddDocumentTab() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { countryCode, docType, addFlow, referenceData } = useSelector((s) => s.dataUpdate)
  const { step, progress, extractedMetadata, extractedHtml, publishedDocId, chunkCount, error, taskId } = addFlow

  const STEPS = [t('du_step_upload'), t('du_step_processing'), t('du_step_review'), t('du_step_publishing'), t('du_step_complete')]
  const STEP_MAP = { upload: 0, processing: 1, review: 2, publishing: 3, complete: 4 }

  const [dragOver, setDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [language, setLanguage] = useState('')
  const [uploading, setUploading] = useState(false)
  const [editedMetadata, setEditedMetadata] = useState(null)
  const [editedHtml, setEditedHtml] = useState(null)
  const [duplicateCheck, setDuplicateCheck] = useState({ open: false, loading: false, existing: null })

  // Initialize edited state when review becomes ready
  React.useEffect(() => {
    if (step === 'review' && extractedMetadata && !editedMetadata) {
      const meta = { ...extractedMetadata, country_code: countryCode, language: language || extractedMetadata.language || 'fr' }
      const cc = countryCode.toUpperCase()
      // Map extracted doc_type (e.g. "arrete") to legislation_type_code with country prefix (e.g. "MA_ARRETE")
      if (docType === 'legislation' && extractedMetadata.doc_type && !meta.legislation_type_code) {
        const raw = extractedMetadata.doc_type.toUpperCase()
        const prefixed = `${cc}_${raw}`
        const types = referenceData?.data?.legislation_types || []
        // Match prefixed code, or fallback to raw if already prefixed
        meta.legislation_type_code = types.find((lt) => lt.code === prefixed)?.code
          || types.find((lt) => lt.code === raw)?.code
          || types.find((lt) => lt.code.toLowerCase().endsWith(`_${extractedMetadata.doc_type.toLowerCase()}`))?.code
          || ''
      }
      // Map jurisdiction: default to {CC}_NATIONAL for unitary states like Morocco
      if (!meta.jurisdiction_code) {
        const jurisdictions = referenceData?.data?.jurisdictions || []
        const extracted = extractedMetadata.jurisdiction
        if (extracted) {
          const prefixed = `${cc}_${extracted.toUpperCase()}`
          meta.jurisdiction_code = jurisdictions.find((j) => j.code === prefixed)?.code
            || jurisdictions.find((j) => j.code === extracted)?.code
            || ''
        }
        // Fallback: if only one jurisdiction for this country, use it
        if (!meta.jurisdiction_code && jurisdictions.length === 1) {
          meta.jurisdiction_code = jurisdictions[0].code
        }
      }
      meta.doc_type = docType
      setEditedMetadata(meta)
      setEditedHtml(extractedHtml || '')
    }
  }, [step, extractedMetadata, extractedHtml, countryCode, docType, language, editedMetadata])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) setSelectedFile(file)
  }, [])

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) setSelectedFile(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setUploading(true)
    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('country_code', countryCode)
    formData.append('doc_type', docType)
    formData.append('language', language)
    await dispatch(uploadDocument(formData))
    setUploading(false)
  }

  const handlePublish = async () => {
    if (!editedMetadata || !editedHtml) return

    // Check for duplicate before publishing
    const docNumber = docType === 'case_law' ? editedMetadata.case_number : editedMetadata.doc_number
    if (docNumber) {
      setDuplicateCheck((prev) => ({ ...prev, loading: true }))
      try {
        const result = await backendClient.checkDataUpdateDuplicate({
          doc_type: docType,
          country_code: countryCode,
          ...(docType === 'case_law' ? { case_number: docNumber } : { doc_number: docNumber }),
        })
        if (result.exists && result.document) {
          setDuplicateCheck({ open: true, loading: false, existing: result.document })
          return // Wait for user confirmation
        }
      } catch (e) {
        console.error('Duplicate check failed, proceeding:', e)
      }
      setDuplicateCheck({ open: false, loading: false, existing: null })
    }

    dispatch(publishDocument(taskId, editedMetadata, editedHtml))
  }

  const handleOverwrite = () => {
    if (!editedMetadata || !editedHtml || !duplicateCheck.existing) return
    const existingMeta = duplicateCheck.existing.metadata
    // Use the existing document's ID so upsert updates instead of creating a new record
    // Set overwrite flag so backend deletes old chunks from Azure Search
    const overwriteMetadata = {
      ...editedMetadata,
      overwrite: true,
      external_id: existingMeta.external_id || existingMeta.case_id,
      document_id: existingMeta.external_id || existingMeta.case_id,
      ...(docType === 'case_law' && { case_id: existingMeta.case_id }),
    }
    setDuplicateCheck({ open: false, loading: false, existing: null })
    dispatch(publishDocument(taskId, overwriteMetadata, editedHtml))
  }

  const handleCancelOverwrite = () => {
    setDuplicateCheck({ open: false, loading: false, existing: null })
  }

  const handleReset = () => {
    dispatch(resetAddFlow())
    setSelectedFile(null)
    setEditedMetadata(null)
    setEditedHtml(null)
    setDuplicateCheck({ open: false, loading: false, existing: null })
  }

  const languages = referenceData.data?.languages || ['ar', 'fr']

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={STEP_MAP[step] || 0} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={handleReset}>
          {error}
        </Alert>
      )}

      {/* Step: Upload */}
      {step === 'upload' && (
        <Box>
          <Paper
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{
              p: 6, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              border: '2px dashed',
              borderColor: dragOver ? '#c9a96e' : 'divider',
              backgroundColor: dragOver ? 'rgba(201, 169, 110, 0.05)' : 'transparent',
              borderRadius: 2, transition: 'all 0.2s',
              '&:hover': { borderColor: '#c9a96e' },
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.html,.htm,.txt"
              hidden
              onChange={handleFileSelect}
            />
            <Upload size={48} color="#c9a96e" style={{ marginBottom: 16 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {selectedFile ? selectedFile.name : t('du_drag_drop')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('du_supported_formats')}
            </Typography>
            {selectedFile && (
              <Chip
                label={`${(selectedFile.size / 1024).toFixed(1)} KB`}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Paper>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('du_language')}</InputLabel>
              <Select value={language} label={t('du_language')} onChange={(e) => setLanguage(e.target.value)}>
                {languages.map((l) => (
                  <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={!selectedFile || !language || uploading}
              onClick={handleUpload}
              startIcon={uploading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <Upload size={16} />}
              sx={{
                backgroundColor: '#c9a96e',
                '&:hover': { backgroundColor: '#b8955a' },
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {uploading ? t('du_uploading') || 'Chargement...' : t('du_upload_process')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Step: Processing */}
      {step === 'processing' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#c9a96e', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {progress.message || t('du_processing_message')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress.percent || 0}
            sx={{
              maxWidth: 400, mx: 'auto', mt: 2, height: 8, borderRadius: 4,
              '& .MuiLinearProgress-bar': { backgroundColor: '#c9a96e' },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {progress.step ? t(progress.step) : ''}
          </Typography>
        </Box>
      )}

      {/* Step: Review */}
      {step === 'review' && editedMetadata && (
        <Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Left: Metadata form */}
            <Box sx={{ flex: '0 0 45%', maxHeight: '70vh', overflow: 'auto' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{t('du_metadata')}</Typography>
              <MetadataEditor
                metadata={editedMetadata}
                onChange={setEditedMetadata}
                docType={docType}
                referenceData={referenceData.data}
              />
            </Box>

            {/* Right: HTML editor / preview */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('du_html_content')}</Typography>
              <RichHtmlEditor
                value={editedHtml}
                onChange={setEditedHtml}
                maxHeight="65vh"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={handleReset}
              sx={{ textTransform: 'none' }}
            >
              {t('du_back')}
            </Button>
            <Button
              variant="contained"
              disabled={duplicateCheck.loading}
              endIcon={duplicateCheck.loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <ArrowRight size={16} />}
              onClick={handlePublish}
              sx={{
                backgroundColor: '#c9a96e',
                '&:hover': { backgroundColor: '#b8955a' },
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {duplicateCheck.loading ? (t('du_checking') || 'Checking...') : t('du_publish_document')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Step: Publishing */}
      {step === 'publishing' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#c9a96e', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {progress.message || t('du_publishing_message')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress.percent || 0}
            sx={{
              maxWidth: 400, mx: 'auto', mt: 2, height: 8, borderRadius: 4,
              '& .MuiLinearProgress-bar': { backgroundColor: '#c9a96e' },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {progress.step ? t(progress.step) : ''}
          </Typography>
        </Box>
      )}

      {/* Step: Complete */}
      {step === 'complete' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CheckCircle size={64} color="#4caf50" style={{ marginBottom: 16 }} />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            {t('du_document_published')}
          </Typography>
          {publishedDocId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('du_document_id')}: {publishedDocId}
            </Typography>
          )}
          {chunkCount && (
            <Chip label={t('du_chunks_indexed', { count: chunkCount })} color="success" variant="outlined" />
          )}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<RotateCcw size={16} />}
              onClick={handleReset}
              sx={{
                backgroundColor: '#c9a96e',
                '&:hover': { backgroundColor: '#b8955a' },
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {t('du_add_another')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Duplicate confirmation dialog */}
      <Dialog open={duplicateCheck.open} onClose={handleCancelOverwrite} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={24} color="#ed6c02" />
          {t('du_duplicate_found') || 'Document already exists'}
        </DialogTitle>
        <DialogContent dividers>
          {duplicateCheck.existing && (() => {
            const m = duplicateCheck.existing.metadata
            const rows = docType === 'case_law'
              ? [
                  ['Case Number', m.case_number],
                  ['Court', m.court_name],
                  ['Judgment Date', m.judgment_date],
                  ['Subject', m.subject_matter],
                  ['Decision Type', m.decision_type],
                ]
              : [
                  ['Title', m.title],
                  ['Doc Number', m.doc_number],
                  ['Issue Date', m.issue_date],
                  ['Legislation Type', m.legislation_type_code],
                  ['Authority', m.authority_name || m.authority_type],
                  ['Legal Domain', m.legal_domain],
                ]
            return (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('du_duplicate_message') || 'A document with this number already exists in the database. Do you want to overwrite it?'}
                </Typography>
                <Table size="small">
                  <TableBody>
                    {rows.filter(([, v]) => v).map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 600, width: 160, color: 'text.secondary' }}>{label}</TableCell>
                        <TableCell>{value}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Language</TableCell>
                      <TableCell>{(m.language || '').toUpperCase()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Source</TableCell>
                      <TableCell>{m.source_platform || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Chunks</TableCell>
                      <TableCell>{m.chunk_count ?? '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                      <TableCell>
                        <Chip
                          label={m.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={m.is_active ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelOverwrite} sx={{ textTransform: 'none' }}>
            {t('du_cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleOverwrite}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('du_overwrite') || 'Overwrite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
